<template>
  <v-container>
    <v-layout row>
      <v-flex xs12 sm8 offset-sm2>
        <div class="my-pieces">
          <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>
          <h1>Corps pieces</h1>
          <p class="corps-intro">
            Shared repertory roles and rehearsal videos live here — separate from your showcase solos.
          </p>

          <div v-if="costumeOrderOpen" class="costume-order-card">
            <div class="piece-info">
              <h2>Costume order</h2>
              <p class="piece-meta">Order or borrow corps costume pieces</p>
            </div>
            <router-link to="/costume-order" class="piece-link">
              Open costume order →
            </router-link>
          </div>
          <div v-else class="costume-order-card costume-order-card--soon">
            <div class="piece-info">
              <h2>Costume order</h2>
              <p class="piece-meta">We'll open this when corps costumes are ready</p>
            </div>
            <span class="piece-link piece-link--soon">Coming soon</span>
          </div>

          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <div v-else>
            <div v-if="participatingPieces.length === 0" class="empty-state">
              <p>You haven't been assigned to any corps pieces yet. Check back after casting.</p>
            </div>

            <template v-for="piece in participatingPieces">
              <!-- Piece with a page -->
              <div v-if="piece.route" :key="piece.key" class="piece-card">
                <div class="piece-info">
                  <h2>{{ piece.name }}</h2>
                  <p v-if="piece.meta" class="piece-meta">{{ piece.meta }}</p>
                </div>
                <router-link :to="piece.route" class="piece-link">
                  View details →
                </router-link>
              </div>

              <!-- Coming soon -->
              <div v-else :key="piece.key" class="piece-card piece-card--soon">
                <div class="piece-info">
                  <h2>{{ piece.name }}</h2>
                  <p class="piece-meta">Coming soon</p>
                </div>
                <span class="piece-link piece-link--soon">Coming soon</span>
              </div>
            </template>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style scoped>
.my-pieces {
  padding: 40px 20px 60px;
  color: var(--iabf-ink, #1c1b1f);
}
.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
  display: inline-block;
  margin-bottom: 12px;
  transition: color 0.15s;
}
.back-link:hover {
  color: var(--iabf-ink, #1c1b1f);
}
h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -.02em;
  margin-bottom: 8px;
}
.corps-intro {
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 24px;
  line-height: 1.5;
}
.costume-order-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  margin-bottom: 20px;
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
}
.costume-order-card--soon {
  opacity: 0.72;
}
h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 22px;
  letter-spacing: -.01em;
  margin: 0;
}
.empty-state p {
  font-family: var(--iabf-font-body, inherit);
  font-size: 18px;
  color: rgba(40, 26, 23, .6);
}
.piece-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  border-bottom: 1px solid rgba(0,0,0,.1);
}
.piece-card--soon {
  opacity: 0.45;
}
.piece-meta {
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: rgba(40, 26, 23, .5);
  margin: 4px 0 0;
}
.piece-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 17px;
  color: var(--iabf-primary, #8a957d);
  text-decoration: none;
  white-space: nowrap;
}
.piece-link:hover {
  text-decoration: underline;
}
.piece-link--soon {
  color: rgba(40, 26, 23, .4);
  cursor: default;
  font-size: 15px;
}
</style>

<script>
import { IABF_DANCER_YEAR, hasCorpsCostumeOrder } from '@/config/iabf-event'

const PIECE_CONFIG = [
  { key: 'waltzOfHours',      name: 'Waltz of the Hours',  route: '/woh-videos',    meta: 'Your personal rehearsal videos and staging notes' },
  { key: 'aliceInWonderland', name: 'Alice in Wonderland', route: '/qoh-videos',    meta: 'Your rehearsal videos and staging notes' },
  { key: 'leJazzHot',         name: 'Le Jazz Hot',         route: '/jazz-videos',   meta: 'Your rehearsal videos and costume notes' },
  { key: 'becoming',          name: 'Becoming',            route: '/becoming-videos', meta: 'Your rehearsal videos and stage formations' },
  { key: 'garlandWaltz',      name: 'Garland Waltz',       route: '/gw-videos',     meta: 'Your rehearsal videos and staging notes' },
]

export default {
  computed: {
    loading () {
      return this.$store.getters.loading
    },
    currentIABFDancer () {
      return this.$store.getters.currentIABFDancer
    },
    dancerId () {
      return this.currentIABFDancer ? this.currentIABFDancer.id : null
    },
    costumeOrderOpen () {
      const year = Number(this.$store.getters.iabfDancerYear) ||
        Number(this.currentIABFDancer && this.currentIABFDancer.festivalYear) ||
        IABF_DANCER_YEAR
      return hasCorpsCostumeOrder(year)
    },
    participatingPieces () {
      const dancer = this.currentIABFDancer
      if (!dancer) return []
      return PIECE_CONFIG.filter(p => dancer[p.key] && dancer[p.key].participating)
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
  }
}
</script>
